import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import { useEffect, useRef, useMemo, useCallback } from "react";
import { useNotificationStream } from "./useNotificationStream";
import { useAuth } from "../contexts/AuthContext";
import {
  requestNotificationPermission,
  showBrowserNotification,
  hasNotificationPermission,
} from "../utils/browserNotifications";
import {
  getNotificationId,
  isSellerRelevantNotification,
  SellerNotification,
} from "../types/notifications";
import api from "../services/api";
import { LIVE_POLL, LIVE_STALE_MS, livePollInterval } from "../config/liveSync";
import {
  invalidateSellerLiveData,
  notificationTypeToInvalidateScope,
} from "../lib/invalidateSellerLive";

const EMPTY_NOTIFICATIONS: SellerNotification[] = [];

export interface UnreadSummary {
  total: number;
  messages: number;
  offers: number;
  orders: number;
  payments: number;
}

const EMPTY_SUMMARY: UnreadSummary = {
  total: 0,
  messages: 0,
  offers: 0,
  orders: 0,
  payments: 0,
};

/**
 * Seller notifications — server is source of truth; polling with optional SSE.
 */
export const useNotifications = () => {
  const queryClient = useQueryClient();
  const { token, user } = useAuth();
  const isAuthenticated = !!token && !!user;

  const knownIdsRef = useRef(new Set<string>());
  const isFirstLoadRef = useRef(true);

  useNotificationStream();

  useEffect(() => {
    if (isAuthenticated && !hasNotificationPermission()) {
      requestNotificationPermission();
    }
  }, [isAuthenticated]);

  const { data: summaryData, refetch: refetchSummary } = useQuery({
    queryKey: ["notifications-unread-summary"],
    queryFn: async (): Promise<UnreadSummary> => {
      try {
        const response = await api.get('/notifications/unread-summary?target=seller');
        const data = response.data?.data;
        if (data && typeof data.total === 'number') {
          return {
            total: data.total,
            messages: data.messages ?? 0,
            offers: data.offers ?? 0,
            orders: data.orders ?? 0,
            payments: data.payments ?? 0,
          };
        }
      } catch (error) {
        console.error('Failed to fetch unread summary:', error);
      }
      return EMPTY_SUMMARY;
    },
    enabled: !!isAuthenticated,
    staleTime: LIVE_STALE_MS,
    refetchInterval: () => livePollInterval(LIVE_POLL.NOTIFICATIONS_SUMMARY),
    placeholderData: keepPreviousData,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
  });

  const {
    data: notificationsData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => {
      try {
        const response = await api.get('/notifications?target=seller');
        if (response.data?.status === "success" && response.data?.data) {
          const fetchedNotifications = response.data.data;

          return (Array.isArray(fetchedNotifications)
            ? fetchedNotifications
            : fetchedNotifications.notifications || [])
            .filter((n: SellerNotification) => isSellerRelevantNotification(n));
        }
        return [];
      } catch (fetchError: unknown) {
        const err = fetchError as { code?: string };
        if (err.code !== 'ERR_NETWORK' && err.code !== 'ECONNREFUSED') {
          console.error("Failed to fetch notifications:", fetchError);
        }
        return [];
      }
    },
    enabled: !!isAuthenticated,
    staleTime: LIVE_STALE_MS,
    gcTime: 1000 * 60 * 10,
    refetchInterval: () => livePollInterval(LIVE_POLL.NOTIFICATIONS),
    placeholderData: keepPreviousData,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    refetchOnMount: true,
    retry: false,
  });

  const notifications = notificationsData ?? EMPTY_NOTIFICATIONS;
  const unreadSummary = summaryData ?? EMPTY_SUMMARY;

  useEffect(() => {
    if (!notifications.length) return;

    if (isFirstLoadRef.current) {
      notifications.forEach((n: SellerNotification) => {
        const id = getNotificationId(n);
        if (id) knownIdsRef.current.add(id);
      });
      isFirstLoadRef.current = false;
      return;
    }

    notifications.forEach((n: SellerNotification) => {
      const id = getNotificationId(n);
      if (!id || knownIdsRef.current.has(id)) return;
      knownIdsRef.current.add(id);

      if (!n.isRead && hasNotificationPermission() && !document.hasFocus()) {
        showBrowserNotification(n.title, {
          body: n.message || n.body,
          icon: '/logo192.png',
          tag: `notification-${id}`,
          link: n.link || n.actionUrl || '/orders',
          data: n.payload || n.data,
        });
      }

      const scope = notificationTypeToInvalidateScope(n.type);
      if (scope) {
        void invalidateSellerLiveData(queryClient, scope);
      }
    });
  }, [notifications, queryClient]);

  const unreadCount = useMemo(() => {
    const localUnread = notifications.filter((n: SellerNotification) => !n.isRead).length;
    return Math.max(unreadSummary.total, localUnread);
  }, [notifications, unreadSummary.total]);

  const markAsReadMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await api.patch(`/notifications/${notificationId}/read`);
      return response.data;
    },
    onSuccess: (_data: unknown, notificationId: string) => {
      queryClient.setQueryData(["notifications"], (old: SellerNotification[] = []) =>
        old.map((n) =>
          getNotificationId(n) === notificationId ? { ...n, isRead: true } : n
        )
      );
      queryClient.setQueryData(["notifications-unread-summary"], (old: UnreadSummary | undefined) => {
        const base = old ?? EMPTY_SUMMARY;
        const note = notifications.find(
          (n: SellerNotification) => getNotificationId(n) === notificationId
        );
        if (!note || note.isRead) return base;
        const type = note.type || '';
        const next = { ...base, total: Math.max(0, base.total - 1) };
        if (type === 'message') next.messages = Math.max(0, base.messages - 1);
        else if (type === 'offer' || type === 'bid') next.offers = Math.max(0, base.offers - 1);
        else if (type === 'order') next.orders = Math.max(0, base.orders - 1);
        else if (type === 'payment') next.payments = Math.max(0, base.payments - 1);
        return next;
      });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: async () => {
      const response = await api.patch('/notifications/read-all?target=seller');
      return response.data;
    },
    onSuccess: () => {
      queryClient.setQueryData(["notifications"], (old: SellerNotification[] = []) =>
        old.map((n) => ({ ...n, isRead: true }))
      );
      queryClient.setQueryData(["notifications-unread-summary"], EMPTY_SUMMARY);
    },
  });

  const deleteNotificationMutation = useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await api.delete(`/notifications/${notificationId}`);
      return response.data;
    },
    onSuccess: (_data: unknown, notificationId: string) => {
      const removed = notifications.find(
        (n: SellerNotification) => getNotificationId(n) === notificationId
      );
      queryClient.setQueryData(["notifications"], (old: SellerNotification[] = []) =>
        old.filter((n: SellerNotification) => getNotificationId(n) !== notificationId)
      );
      if (removed && !removed.isRead) {
        queryClient.setQueryData(["notifications-unread-summary"], (old: UnreadSummary | undefined) => {
          const base = old ?? EMPTY_SUMMARY;
          const type = removed.type || '';
          const next = { ...base, total: Math.max(0, base.total - 1) };
          if (type === 'message') next.messages = Math.max(0, base.messages - 1);
          else if (type === 'offer' || type === 'bid') next.offers = Math.max(0, base.offers - 1);
          else if (type === 'order') next.orders = Math.max(0, base.orders - 1);
          else if (type === 'payment') next.payments = Math.max(0, base.payments - 1);
          return next;
        });
      }
    },
  });

  const refetchAll = useCallback(async () => {
    await Promise.all([refetch(), refetchSummary()]);
  }, [refetch, refetchSummary]);

  return {
    notifications,
    unreadCount,
    unreadSummary,
    isLoading,
    error,
    refetch: refetchAll,
    markAsRead: markAsReadMutation.mutateAsync,
    markAllAsRead: markAllAsReadMutation.mutateAsync,
    deleteNotification: deleteNotificationMutation.mutateAsync,
    isMarkingAsRead: markAsReadMutation.isPending,
    isDeleting: deleteNotificationMutation.isPending,
  };
};

export default useNotifications;
